"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Globe, Check } from "lucide-react";
import { useTranslation, LOCALES } from "@/lib/i18n";
import { cn } from "@/lib/utils";

type Variant = "shell" | "ghost";

interface Props {
  /** "shell" = white-on-navy icon button for the dark header. "ghost" = neutral surface button. */
  variant?: Variant;
  className?: string;
}

export function LanguageSwitcher({ variant = "shell", className }: Props) {
  const { locale, setLocale, t, dir } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      if (wrapRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    function update() {
      const el = wrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const panelHeight = panelRef.current?.offsetHeight ?? 400;
      const spaceBelow = window.innerHeight - r.bottom - 8;
      const spaceAbove = r.top - 8;
      const openAbove = spaceBelow < panelHeight && spaceAbove > spaceBelow;
      const top = openAbove ? r.top - panelHeight - 4 : r.bottom + 4;
      const width = 224; // w-56 = 14rem = 224px
      // Anchor the panel to the trigger's start edge: right-align in LTR (button
      // sits on the right of the header), left-align in RTL (button mirrors to
      // the left), clamped into the viewport.
      const rawLeft = dir === "rtl" ? r.left : r.right - width;
      const left = Math.min(Math.max(8, rawLeft), Math.max(8, window.innerWidth - width - 8));
      setPos({ top, left, width });
    }
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, dir]);

  const current = LOCALES.find((l) => l.code === locale) ?? LOCALES[0];
  const label = t("header.language");
  const chooseLabel = t("header.chooseLanguage");

  const triggerCls =
    variant === "shell"
      ? "relative h-9 px-2.5 rounded-xl flex items-center gap-1.5 text-white/85 hover:text-white hover:bg-[color:var(--shell-hover)] transition-colors"
      : "relative h-9 px-2.5 rounded-xl flex items-center gap-1.5 text-foreground hover:bg-muted transition-colors";

  return (
    <div className={cn("relative", className)} ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={label}
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={triggerCls}
        data-testid="language-switcher-trigger"
      >
        <Globe className="w-[18px] h-[18px]" />
        <span className="text-footnote font-semibold uppercase tracking-wide">{current.code}</span>
      </button>

      {open && pos && typeof document !== "undefined" && createPortal(
        <div
          ref={panelRef}
          role="listbox"
          aria-label={chooseLabel}
          style={{ position: "fixed", top: pos.top, left: pos.left, width: pos.width }}
          className="z-50 max-h-[60vh] overflow-y-auto rounded-xl border border-border glass shadow-soft-lg py-1.5 anim-slide-down"
        >
          <div className="px-3 py-2 text-caption uppercase tracking-wider text-muted-foreground border-b border-border/60 mb-1">
            {chooseLabel}
          </div>
          {LOCALES.map((l) => {
            const active = l.code === locale;
            return (
              <button
                key={l.code}
                role="option"
                aria-selected={active}
                onClick={() => {
                  setLocale(l.code);
                  setOpen(false);
                }}
                className={cn(
                  "w-full mx-1.5 px-2.5 py-2 rounded-lg flex items-center justify-between gap-3 text-body transition-colors",
                  active ? "bg-muted text-foreground font-semibold" : "text-foreground hover:bg-muted",
                )}
                style={{ width: "calc(100% - 12px)" }}
                data-testid={`language-option-${l.code}`}
              >
                <span className="flex items-center gap-2 min-w-0">
                  <span aria-hidden="true" className="text-[15px] leading-none">{l.flag}</span>
                  <span className="truncate">{l.label}</span>
                  <span className="text-caption uppercase tracking-wide text-muted-foreground">{l.code}</span>
                </span>
                {active && <Check className="w-4 h-4 text-primary shrink-0" aria-hidden="true" />}
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
}
